import { useNavigate } from 'react-router-dom';
import { CheckCircledIcon, CircleIcon } from '@radix-ui/react-icons';
import { Card, CardContent, Button } from '../../ui';
import PATHS from '../../../constants/Paths';

/**
 * The first screen, for a workspace with nothing in it yet.
 *
 * It used to be a single button to /scans/new - a form that needs a client and
 * an account, neither of which exists. The very first instruction could not be
 * followed. This is the actual sequence, with the steps already done ticked, so
 * a half-set-up workspace is told what is left rather than starting over.
 */
export function GettingStarted({ clientCount = 0, accountCount = 0, scanCount = 0 }) {
	const navigate = useNavigate();

	const steps = [
		{
			done: clientCount > 0,
			title: 'Add a client',
			description: 'The person or brand whose reputation you look after.',
			action: 'Add a client',
			to: PATHS.CLIENT_LIST,
		},
		{
			done: accountCount > 0,
			title: 'Add one of their accounts',
			description: 'An X, TikTok or Reddit handle to watch.',
			action: 'Add an account',
			to: PATHS.CLIENT_LIST,
		},
		{
			done: scanCount > 0,
			title: 'Run the first scan',
			description: "We'll read their recent posts and flag anything risky.",
			action: 'New scan',
			to: PATHS.NEW_SCANS,
		},
	];

	const next = steps.find((s) => !s.done);

	return (
		<Card>
			<CardContent className="p-6 sm:p-8">
				<h2 className="text-xl font-semibold text-foreground">Welcome to RepCheck</h2>
				<p className="mt-1 max-w-prose text-sm text-muted-foreground">
					Three steps and you'll be monitoring your first account.
				</p>

				<ol className="mt-6 space-y-4">
					{steps.map((step) => {
						const isNext = step === next;
						return (
							<li key={step.title} className="flex items-start gap-3">
								{step.done
									? <CheckCircledIcon className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
									: <CircleIcon className={`mt-0.5 h-5 w-5 shrink-0 ${isNext ? 'text-brand-600' : 'text-muted-foreground'}`} />}
								<div className="min-w-0 flex-1">
									<div className={`text-sm font-medium ${step.done ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
										{step.title}
									</div>
									{!step.done && <p className="text-sm text-muted-foreground">{step.description}</p>}
								</div>
								{isNext && (
									<Button size="sm" onClick={() => navigate(step.to)}>{step.action}</Button>
								)}
							</li>
						);
					})}
				</ol>
			</CardContent>
		</Card>
	);
}
